import React, { useMemo, useState } from "react";
import { useQuery } from "urql";
import { Link, useParams } from "wouter";
import { Form, Formik } from "formik";
import stableStringify from "json-stable-stringify";

import { Button } from "../../../../components/src/Button";
import { DateField, Label, NumberField, TextField } from "../../../../components/src/form";
import { QUERY_TRANSFER_AGENT_TOTAL_UNITS } from "../../../../graphql/src/transferagent/queries";
import { TransferAgentTotalUnit } from "../../types.generated";

export const TotalUnitPage = (): React.ReactElement => {
    const params = useParams<{ id: string }>();
    const id = params.id;
    const [showVersions, setShowVersions] = useState(false);

    const [{ fetching, error, data }] = useQuery<{ transferAgentTotalUnits?: Partial<TransferAgentTotalUnit>[] }>({
        query: QUERY_TRANSFER_AGENT_TOTAL_UNITS,
        requestPolicy: "cache-and-network",
        variables: { filter: { idIn: [id] }, includeVersions: showVersions }
    });

    const { totalUnit, versions } = useMemo(() => {
        if (!data?.transferAgentTotalUnits?.length) {
            return { totalUnit: null, versions: [] };
        }
        const unit = data.transferAgentTotalUnits[0];
        return {
            totalUnit: unit,
            versions: unit.versions || []
        };
    }, [data?.transferAgentTotalUnits]);

    if (fetching && !data) return <div>Loading...</div>;
    if (error)
        return (
            <div>
                <p>error:</p>
                <pre>{JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    if (!totalUnit)
        return (
            <div>
                <p>No total units found</p>
            </div>
        );

    const initialValues = {
        ...totalUnit,
        instrumentName: totalUnit.instrument ? totalUnit.instrument.name : null,
        sourceName: totalUnit.source ? totalUnit.source.name : null
    };

    return (
        <div>
            <div className="mb-2">
                <Link to="/reconciliation">Units reconciliation</Link>
            </div>

            <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={() => {}}>
                {() => (
                    <Form autoComplete="off">
                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <DateField name="date" label="Date" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <NumberField name="totalUnits" label="Total units" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField
                                    name="instrumentName"
                                    label={
                                        totalUnit.instrument ? (
                                            <Label
                                                name="instrumentName"
                                                value={<Link to={`/instruments/${totalUnit.instrument._id}`}>Instrument</Link>}
                                            />
                                        ) : (
                                            "Instrument"
                                        )
                                    }
                                    className=""
                                    disabled={true}
                                />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="isin" label="ISIN" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField
                                    name="sourceName"
                                    label={
                                        totalUnit.source ? (
                                            <Label name="sourceName" value={<Link to={`/parties/${totalUnit.source._id}`}>Source</Link>} />
                                        ) : (
                                            "Source"
                                        )
                                    }
                                    className=""
                                    disabled={true}
                                />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="sourceId" label="Source id" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full px-2 mb-4">
                                <TextField name="_id" label="Id" className="" disabled={true} />
                            </div>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="createTimestamp" label="Create timestamp" className="" disabled={true} />
                            </div>
                            <div className="w-full md:w-1/2 px-2 mb-4">
                                <TextField name="updateTimestamp" label="Update timestamp" className="" disabled={true} />
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

            <Button variant="primary" onClick={() => setShowVersions(!showVersions)}>
                {showVersions ? "Hide versions" : "Show versions"}
            </Button>

            {showVersions ? (
                <div className="m-2">
                    <pre>{stableStringify(versions, { space: 2 })}</pre>
                </div>
            ) : null}
        </div>
    );
};
